import {
  Box,
  Button,
  Modal,
  Typography,
  Chip,
  CircularProgress,
} from "@mui/material";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import React, { useState } from "react";
import { useOutletContext } from "react-router-dom";
import {
  getWithQueryApiServices,
  postWithQueryApiServices,
} from "../../../apis/api";
import { apiRoutes } from "../../../apis/apiPath";
import { errorToast, successToast } from "../../../utils/helper";
import PaymentForm from "../../shared/PaymentForm";

/**
 * Tenant payment-method panel: save card/ACH to Basys vault only (not our DB).
 */
export const PaymentMethodPanel = ({ canManage }) => {
  const { tenantString } = useOutletContext();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["invoicePaymentMethod", tenantString],
    queryFn: () =>
      getWithQueryApiServices(
        apiRoutes.INVOICE_PAYMENT_METHOD,
        `x=1${tenantString || ""}`
      ),
    enabled: !!canManage,
  });

  const method = data?.data || {};

  const saveMutation = useMutation({
    mutationFn: (payload) => {
      const formData = payload?.formData || payload;
      return postWithQueryApiServices(
        apiRoutes.INVOICE_PAYMENT_METHOD,
        `x=1${tenantString || ""}`,
        formData
      );
    },
    onSuccess: () => {
      successToast("Payment method saved");
      setOpen(false);
      queryClient.invalidateQueries({ queryKey: ["invoicePaymentMethod"] });
      queryClient.invalidateQueries({ queryKey: ["invoiceAlerts"] });
      refetch();
    },
    onError: (err) => {
      errorToast(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to save payment method"
      );
    },
  });

  if (!canManage) return null;

  const label = method.hasPaymentMethod
    ? method.type === "ach"
      ? `Bank •••• ${method.last4 || "????"}`
      : method.type === "card"
        ? `Card •••• ${method.last4 || "????"}`
        : "Payment method on file"
    : "No payment method on file";

  return (
    <Box
      sx={{
        mb: 2,
        p: 2,
        borderRadius: 2,
        bgcolor: "#f5f5f5",
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        alignItems: { xs: "flex-start", sm: "center" },
        justifyContent: "space-between",
        gap: 2,
      }}
    >
      <Box>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
          Auto-pay payment method
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Stored with the payment gateway only. Used to auto-charge monthly
          invoices for all companies under this tenant.
        </Typography>
        <Box sx={{ mt: 1 }}>
          {isLoading ? (
            <CircularProgress size={18} />
          ) : (
            <Chip
              size="small"
              color={method.hasPaymentMethod ? "success" : "default"}
              label={label}
            />
          )}
        </Box>
      </Box>
      <Button variant="outlined" onClick={() => setOpen(true)}>
        {method.hasPaymentMethod ? "Replace" : "Add"} payment method
      </Button>

      <Modal open={open} onClose={() => setOpen(false)}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: 400,
            bgcolor: "background.paper",
            boxShadow: 24,
            p: 4,
            borderRadius: 2,
          }}
        >
          <Typography variant="h6" sx={{ mb: 2 }}>
            {method.hasPaymentMethod ? "Replace" : "Add"} payment method
          </Typography>
          <PaymentForm
            onSubmit={(arg) => saveMutation.mutate(arg)}
            isSubmitting={saveMutation.isPending}
            submitLabel="Save payment method"
          />
        </Box>
      </Modal>
    </Box>
  );
};
